import React from 'react'
import { Box, SvgIcon, Typography } from '@mui/material'
import { cardSx, depth, toneTile } from '../../utils/depth'

/** Raised gradient tile holding an MUI icon (or an SVG component). */
export const IconTile = ({ icon: Icon, tone = 'orange', size = 44, sx }) => {
  if (!Icon) return null
  const iconSize = Math.round(size * 0.5)
  return (
    <Box
      sx={{
        flexShrink: 0,
        width: size,
        height: size,
        borderRadius: `${Math.round(size * 0.3)}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...toneTile(tone),
        ...sx
      }}>
      {Icon.muiName ? (
        <Icon sx={{ fontSize: iconSize, color: 'inherit' }} />
      ) : (
        <SvgIcon
          component={Icon}
          sx={{
            fontSize: iconSize,
            color: 'inherit',
            '& path': { fill: 'currentColor' }
          }}
        />
      )}
    </Box>
  )
}

/**
 * Raised card with a tinted title band. Used to split long forms (the store
 * profile, say) into scannable groups.
 */
export const SectionCard = ({
  icon,
  tone,
  title,
  subtitle,
  action,
  children,
  sx,
  bodySx
}) => (
  <Box sx={{ ...cardSx, overflow: 'hidden', mb: 3, ...sx }}>
    {title ? (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.75,
          px: { xs: 2, sm: 3 },
          py: 2,
          background: depth.headerBackground,
          borderBottom: '1px solid rgba(16,24,40,0.06)'
        }}>
        <IconTile icon={icon} tone={tone} size={40} />
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography
            sx={{ fontSize: 16, fontWeight: 700, color: 'text.primary' }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
      </Box>
    ) : null}
    <Box sx={{ px: { xs: 2, sm: 3 }, pt: 1, pb: 3, ...bodySx }}>{children}</Box>
  </Box>
)

/** Stat tile: gradient icon, label, big number and optional hint. */
export const StatTile = ({ icon, tone, label, value, hint }) => (
  <Box
    sx={{
      ...cardSx,
      p: 2.5,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      height: '100%',
      transition: 'transform .18s ease, box-shadow .18s ease',
      '&:hover': { transform: 'translateY(-3px)', boxShadow: depth.cardHover }
    }}>
    <IconTile icon={icon} tone={tone} size={52} />
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{ fontSize: 13, fontWeight: 600, color: 'text.disabled' }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 28,
          fontWeight: 800,
          color: 'text.primary',
          letterSpacing: '-0.02em',
          lineHeight: 1.15
        }}>
        {value}
      </Typography>
      {hint ? (
        <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
          {hint}
        </Typography>
      ) : null}
    </Box>
  </Box>
)

/** Primary button look (gradient, raised, presses in). Spread into `sx`. */
export const raisedButtonSx = {
  background: depth.brandGradient,
  color: 'common.white',
  fontWeight: 700,
  textTransform: 'none',
  borderRadius: '12px',
  px: 2.5,
  minHeight: 44,
  boxShadow: depth.brandShadow,
  transition: 'transform .12s ease, box-shadow .12s ease',
  '&:hover': {
    background: depth.brandGradient,
    boxShadow: depth.brandShadow,
    transform: 'translateY(-1px)'
  },
  '&:active': {
    transform: 'translateY(1px)',
    boxShadow: depth.brandShadowPressed
  },
  '&.Mui-disabled': {
    background: '#E5E7EB',
    color: 'text.disabled',
    boxShadow: 'none'
  }
}
